import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchAPI } from '../lib/api';
import { cn } from '../lib/utils';
import { Search, Bot, Wrench, AlertTriangle, FileText, LayoutDashboard, ArrowRight, Command, X } from 'lucide-react';

const TYPE_ICONS = {
  agent: Bot, tool: Wrench, escalation: AlertTriangle,
  invoice: FileText, page: LayoutDashboard,
};

const TYPE_COLORS = {
  agent: 'text-teal-600 bg-teal-50',
  tool: 'text-blue-600 bg-blue-50',
  escalation: 'text-amber-600 bg-amber-50',
  invoice: 'text-purple-600 bg-purple-50',
  page: 'text-slate-600 bg-slate-100',
};

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [open]);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await searchAPI.query(q);
      setResults(res.data || []);
      setSelectedIndex(0);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => doSearch(query), 250);
    return () => clearTimeout(timer);
  }, [query, doSearch]);

  const handleSelect = (result) => {
    setOpen(false);
    navigate(result.url);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      handleSelect(results[selectedIndex]);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]" data-testid="command-palette">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />

      {/* Palette */}
      <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl border overflow-hidden animate-fade-in-up">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 border-b">
          <Search className="w-5 h-5 text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search agents, tools, invoices, pages..."
            className="flex-1 h-14 text-base bg-transparent outline-none placeholder:text-slate-400"
          />
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[400px] overflow-y-auto p-2">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!loading && query.length >= 2 && results.length === 0 && (
            <div className="text-center py-8">
              <Search className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-400">No results for "{query}"</p>
            </div>
          )}

          {!loading && query.length < 2 && (
            <div className="py-4 px-2">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-3">Quick Navigation</p>
              {[
                { title: 'Agent Catalog', url: '/studio', icon: Bot },
                { title: 'Operations Console', url: '/console', icon: LayoutDashboard },
                { title: 'Escalation Queue', url: '/console/escalations', icon: AlertTriangle },
                { title: 'Executive Dashboard', url: '/portal', icon: FileText },
              ].map((item, i) => (
                <button
                  key={i}
                  onClick={() => { setOpen(false); navigate(item.url); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-slate-50 transition-colors"
                >
                  <item.icon className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-700">{item.title}</span>
                  <ArrowRight className="w-3 h-3 text-slate-300 ml-auto" />
                </button>
              ))}
            </div>
          )}

          {results.map((result, i) => {
            const Icon = TYPE_ICONS[result.type] || Search;
            return (
              <button
                key={`${result.type}-${result.id}`}
                onClick={() => handleSelect(result)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors',
                  i === selectedIndex ? 'bg-teal-50 ring-1 ring-teal-200' : 'hover:bg-slate-50'
                )}
              >
                <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', TYPE_COLORS[result.type])}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{result.title}</p>
                  <p className="text-xs text-slate-400 truncate">{result.subtitle}</p>
                </div>
                <span className="text-[10px] text-slate-300 uppercase tracking-wider shrink-0">{result.type}</span>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t bg-slate-50 flex items-center gap-4 text-[10px] text-slate-400">
          <span className="flex items-center gap-1"><kbd className="bg-white border rounded px-1 py-0.5">↑↓</kbd> navigate</span>
          <span className="flex items-center gap-1"><kbd className="bg-white border rounded px-1 py-0.5">↵</kbd> select</span>
          <span className="flex items-center gap-1"><kbd className="bg-white border rounded px-1 py-0.5">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
